// Lightweight debug helpers that can be toggled at runtime via localStorage

// wsDebug: 'true' enables logging
// wsFocus: comma-separated filter terms. If set, only logs that contain at least one term are printed

export function isWsDebugEnabled(): boolean {
  try {
    if (typeof window !== "undefined" && typeof localStorage !== "undefined") {
      return localStorage.getItem("wsDebug") === "true";
    }
  } catch {
    // Silently ignore - localStorage may not be available (SSR/iframe restrictions)
  }
  return false;
}

export function getWsFocusTokens(): string[] {
  try {
    if (typeof window !== "undefined" && typeof localStorage !== "undefined") {
      const raw = localStorage.getItem("wsFocus") || "";
      return raw
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    }
  } catch {
    // Silently ignore - localStorage may not be available (SSR/iframe restrictions)
  }
  return [];
}

function stringifyArgs(args: any[]): string {
  return args
    .map((a) => {
      if (typeof a === "string") return a;
      try {
        return JSON.stringify(a);
      } catch {
        return String(a);
      }
    })
    .join(" ");
}

function matchesFocus(args: any[]): boolean {
  const tokens = getWsFocusTokens();
  if (tokens.length === 0) return true;
  const haystack = stringifyArgs(args);
  return tokens.some((t) => haystack.includes(t));
}

export function wsLog(...args: any[]): void {
  // Keep available but disabled by default
  if (!isWsDebugEnabled()) return;
  if (!matchesFocus(args)) return;
  console.log(...args);
}

export function wsWarn(...args: any[]): void {
  if (!isWsDebugEnabled()) return;
  if (!matchesFocus(args)) return;
  console.warn(...args);
}

export function wsError(...args: any[]): void {
  // Errors respect focus if set, but still require wsDebug to avoid noise
  if (!isWsDebugEnabled()) return;
  if (!matchesFocus(args)) return;
  console.error(...args);
}

// Optional global console filter when wsFocus is set, to reduce noise without changing call sites
// It only filters console.log/console.warn; errors are untouched.
export function installConsoleFocusFilter(): void {
  try {
    if (typeof window === "undefined") return;
    const w = window as any;
    if (w.__wsConsoleFocusFilterInstalled) return;

    const originalLog = console.log.bind(console);
    const originalWarn = console.warn.bind(console);

    const shouldPass = (args: any[]) => {
      const tokens = getWsFocusTokens();
      if (tokens.length === 0) return true; // no focus ⇒ pass through
      return matchesFocus(args);
    };

    console.log = (...args: any[]) => {
      if (shouldPass(args)) originalLog(...args);
    };
    console.warn = (...args: any[]) => {
      if (shouldPass(args)) originalWarn(...args);
    };

    w.__wsConsoleFocusFilterInstalled = true;
  } catch {
    // Silently ignore - may fail in non-browser environments
  }
}


